<template>
	<TemplateBase
		:is-sales="isSales"
		class="bg-gray-50"
	>
		<div
			class="bg-gradient-to-r from-purple-700 to-purple-900 w-full flex flex-col justify-end items-center pt-24 mb-32"
		>
			<div
				class="container flex flex-col items-center gap-16 max-w-screen-md -mb-16"
			>
				<div class="w-full">
					<LazyAtomBreadcrumbs
						v-if="breadcrumbs"
						:breadcrumbs="breadcrumbs"
						class="text-title-size-xs"
					/>
				</div>
				<div class="flex items-center gap-12 w-full">
					<div class="aspect-square size-60">
						<LazyAtomImage
							v-if="featuredImage"
							v-bind="featuredImage"
							:width="150"
							:height="150"
							fit="contain"
							img-class="rounded ring-purple-800 shrink-0 mt-3 size-full object-cover"
						/>
					</div>
					<div class="grid lg:grid-cols-3 gap-7">
						<div
							class="flex flex-col gap-3"
						>
							<h2 class="text-functional-size-2xs uppercase tracking-wider text-white/50">
								Categorie
							</h2>
							<div class="flex flex-wrap gap-4">
								<AtomLink
									size="sm"
									variant="white"
									:url="category.path"
									:title="category.title"
									class="text-functional-size-sm underline text-white hover:text-purple-200"
								>
									{{ category.title }}
								</AtomLink>
							</div>
						</div>
						<div
							v-if="tags && tags.length > 0"
							class="flex flex-col gap-3"
						>
							<h2 class="text-functional-size-2xs uppercase tracking-wider text-white/50">
								<span v-if="tags.length === 1">Label</span>
								<span v-else>Labels</span>
							</h2>
							<div class="flex flex-wrap gap-4">
								<AtomLabel
									v-for="tag in tags"
									:key="tag"
									size="sm"
									:variant="ColorVariantEnum.Light"
								>
									{{ tag }}
								</AtomLabel>
							</div>
						</div>
						<div
							class="flex flex-col gap-3"
						>
							<h2 class="text-functional-size-2xs uppercase tracking-wider text-white/50">
								Geschreven door
							</h2>
							<div class="flex flex-wrap gap-4 text-white text-functional-size-sm">
								{{ author }}
							</div>
						</div>
						<div
							class="flex flex-col gap-3"
						>
							<h2 class="text-functional-size-2xs uppercase tracking-wider text-white/50">
								Gepubliceerd op
							</h2>
							<div class="flex flex-wrap gap-4 text-white text-functional-size-sm">
								{{ formatDate(publishedAt, 'DD MMMM YYYY') }}
							</div>
						</div>
						<div
							v-if="publishedAt !== updatedAt"
							class="flex flex-col gap-3"
						>
							<h2 class="text-functional-size-2xs uppercase tracking-wider text-white/50">
								Bijgewerkt op
							</h2>
							<div class="flex flex-wrap gap-4 text-white text-functional-size-sm">
								{{ formatDate(updatedAt, 'DD MMMM YYYY') }}
							</div>
						</div>
					</div>
				</div>
				<AtomCard
					class="flex flex-col gap-8 text-center py-16 px-16 w-full"
				>
					<AtomTitle
						tag="h1"
						size="3xl"
					>
						{{ title }}
					</AtomTitle>
					<span class="font-display font-bold text-title-size-lg lg:text-title-size-xl text-purple-700">
						{{ subtitle }}
					</span>
					<p class="text-paragraph-size-lg mb-8">
						{{ description }}
					</p>
				</AtomCard>
			</div>
		</div>
		<div>
			<slot />
		</div>
	</TemplateBase>
</template>

<script lang="ts" setup>
import type { BreadcrumbsType } from '#base/core/types/BreadcrumbsType';
import { ColorVariantEnum } from '#base/core/enums/ColorVariantEnum';
import type { BlogCategoryType } from '#base/core/types/BlogCategoryType';
import type { ImageType } from '#base/core/types/ImageType';
import { formatDate } from '#base/helpers/dateHelpers';
import { formatSchema } from '#base/helpers/schemaHelpers';
import { baseUrl } from '#fysio/core/consts/companyData';

const props = defineProps<{
	breadcrumbs?: BreadcrumbsType;
	isSales?: boolean;
	title: string;
	subtitle: string;
	description: string;
	featuredImage?: ImageType;
	tags?: string[];
	category: BlogCategoryType;
	author: string;
	publishedAt: Date;
	updatedAt: Date;
}>();

const route = useRoute();

useHead({
	script: [
		{
			type: 'application/ld+json',
			innerHTML: formatSchema({
				'@context': 'https://schema.org',
				'@type': 'BlogPosting',
				'url': baseUrl + route.path,
				'dateModified': props.updatedAt ? formatDate(props.updatedAt, 'YYYY-MM-DD') : undefined,
				'datePublished': formatDate(props.publishedAt, 'YYYY-MM-DD'),
				'name': props.title,
				'description': props.description,
				'author': {
					'@type': 'Person',
					'name': props.author,
				},
				'keywords': props.tags,
				'image': props.featuredImage
					? {
							'@type': 'ImageObject',
							'@id': baseUrl + props.featuredImage.src,
							'url': baseUrl + props.featuredImage.src,
							'height': `${props.featuredImage.height}`,
							'width': `${props.featuredImage.width}`,
						}
					: undefined,
			}).innerHTML,
		},
	],
});
</script>
